import {
  GetProgramEnrollmentPaymentPlansDocument,
  GetViewerLearningDetailsDocument,
  PaymentPlanStatus,
  ProgramEnrollmentStatus,
} from '@sandtech-hub-monorepo/ehub-client';
import { useQuery } from '@apollo/client';
import { useCognito } from '@sandtech-hub-monorepo/ui-auth-kit';
import { LearningSkeleton } from '../components/LearningSkeleton';
import { ProgramEnrollmentsEmptyState } from './components/ProgramEnrollmentsEmptyState';
import { useMemo } from 'react';
import { ProgramEnrollmentView } from './components/ProgramEnrollmentView';
import {
  PaymentBannerMessages,
  ProgramEnrollmentPaymentBanner,
} from './components/ProgramEnrollmentPaymentBanner';

const PAYMENT_BANNER_MESSAGES: PaymentBannerMessages = {
  success: (
    <>
      <b>Payment up to date.</b> Your fees are up to date, ensuring
      uninterrupted access to your course. Keep it up!
    </>
  ),
  fallback: {
    red: (
      <>
        <b>Payment required.</b> Please make a payment.
      </>
    ),
    amber: (
      <>
        <b>Payment due soon.</b> Please make a payment.
      </>
    ),
    blue: (
      <>
        <b>Payment coming up.</b> Please make a payment.
      </>
    ),
  },
  beforeFirst: {
    blue: ({ formattedDate, isAllAccess }) => (
      <>
        <b>Payment coming up:</b> Make your first payment by{' '}
        <b>{formattedDate}</b> to ensure uninterrupted learning
        {!isAllAccess && ' and begin the term'}.
      </>
    ),
    amber: () => (
      <>
        <b>Payment due:</b> Complete your first payment to unlock All Access.
      </>
    ),
    red: ({ nextAmount }) => (
      <>
        <b>Payment overdue:</b> Payment of <b>${nextAmount.toFixed(2)}</b> is
        now overdue, please make a payment urgently to avoid disruption to your
        learning.
      </>
    ),
  },
  transitionNoOutstanding: {
    blue: ({ formattedDate, isAllAccess }) => (
      <>
        <b>Payment coming up:</b> Make your first payment by{' '}
        <b>{formattedDate}</b>
        {!isAllAccess && ' to advance to the next term'}.
      </>
    ),
    amber: ({ formattedDate, isAllAccess }) => (
      <>
        <b>Payment due soon:</b> Make your first payment by{' '}
        <b>{formattedDate}</b>
        {!isAllAccess && ' to advance to the next term'}.
      </>
    ),
    red: ({ nextAmount }) => (
      <>
        <b>Payment overdue:</b> Payment of <b>${nextAmount.toFixed(2)}</b> is
        now overdue, please make a payment urgently to avoid disruption to your
        learning.
      </>
    ),
  },
  transitionWithOutstanding: {
    blue: ({ formattedDate, outstandingBalance, isAllAccess }) => (
      <>
        <b>Payment coming up:</b> Make your first payment of <b>$5</b> and clear
        outstanding balance <b>${outstandingBalance.toFixed(2)}</b> by{' '}
        <b>{formattedDate}</b>
        {!isAllAccess && ' to advance to the next term'}.
      </>
    ),
    amber: ({ formattedDate, outstandingBalance, isAllAccess }) => (
      <>
        <b>Payment due soon:</b> Make your first payment of <b>$5</b> and clear
        outstanding balance <b>${outstandingBalance.toFixed(2)}</b> by{' '}
        <b>{formattedDate}</b>
        {!isAllAccess && ' to advance to the next term'}.
      </>
    ),
    red: ({ nextAmount }) => (
      <>
        <b>Payment overdue:</b> Payment of <b>${nextAmount.toFixed(2)}</b> is
        now overdue, please make a payment urgently to avoid disruption to your
        learning.
      </>
    ),
  },
};

type ProgramEnrollmentsViewProps = {
  type: 'current' | 'past' | 'graduated';
  paymentFAQsLink: string;
  onlyOngoingCourses?: boolean;
  showOngoingCoursesToggle?: boolean;
};

export const ProgramEnrollmentsView = ({
  type,
  paymentFAQsLink,
  onlyOngoingCourses = false,
  showOngoingCoursesToggle = false,
}: ProgramEnrollmentsViewProps) => {
  const { currentUser, loading: authLoading } = useCognito();

  const { data, loading } = useQuery(GetViewerLearningDetailsDocument, {
    fetchPolicy: 'cache-first',
    skip: !currentUser,
  });
  const { data: paymentPlansData, loading: paymentPlansLoading } = useQuery(
    GetProgramEnrollmentPaymentPlansDocument,
    {
      fetchPolicy: 'cache-first',
      skip: !currentUser,
    }
  );

  const {
    ongoingProgramEnrollments,
    pastProgramEnrollments,
    graduatedProgramEnrollments,
  } = useMemo(() => {
    const enrollments = data?.viewer.programEnrollments || [];
    const pastStatuses = [
      ProgramEnrollmentStatus.Graduated,
      ProgramEnrollmentStatus.Failed,
    ];

    return {
      ongoingProgramEnrollments: enrollments.filter(
        ({ status }) => status === ProgramEnrollmentStatus.Ongoing
      ),
      pastProgramEnrollments: enrollments.filter(({ status }) =>
        pastStatuses.includes(status)
      ),
      graduatedProgramEnrollments: enrollments.filter(
        ({ status }) => status === ProgramEnrollmentStatus.Graduated
      ),
    };
  }, [data?.viewer.programEnrollments]);

  const hasOngoingProgramEnrollments = ongoingProgramEnrollments.length > 0;
  const hasPastProgramEnrollments = pastProgramEnrollments.length > 0;
  const hasGraduatedProgramEnrollments = graduatedProgramEnrollments.length > 0;

  if (loading || authLoading || paymentPlansLoading) {
    return <LearningSkeleton />;
  }

  return (
    <>
      {!hasOngoingProgramEnrollments && type === 'current' && (
        <ProgramEnrollmentsEmptyState />
      )}
      {hasOngoingProgramEnrollments && type === 'current' && (
        <>
          {paymentPlansData?.viewer?.paymentPlans
            ?.filter(({ status }) => status !== PaymentPlanStatus.Cancelled)
            .map((paymentPlan) => (
              <ProgramEnrollmentPaymentBanner
                key={paymentPlan.paymentPlanId}
                paymentPlan={paymentPlan}
                paymentFAQsLink={paymentFAQsLink}
                messages={PAYMENT_BANNER_MESSAGES}
              />
            ))}
          {paymentPlansData?.viewer?.paymentPlans.length === 0 &&
            ongoingProgramEnrollments.find(
              ({ program }) => program.allAccessPaymentPlan
            ) && (
              <ProgramEnrollmentPaymentBanner
                paymentFAQsLink={paymentFAQsLink}
                messages={PAYMENT_BANNER_MESSAGES}
              />
            )}
          {ongoingProgramEnrollments.map((enrollment) => (
            <ProgramEnrollmentView
              enrollment={enrollment}
              key={enrollment.id}
              onlyOngoingCourses={onlyOngoingCourses}
              showOngoingCoursesToggle={showOngoingCoursesToggle}
            />
          ))}
        </>
      )}
      {hasPastProgramEnrollments &&
        type === 'past' &&
        pastProgramEnrollments.map((enrollment) => (
          <ProgramEnrollmentView enrollment={enrollment} key={enrollment.id} />
        ))}
      {!hasPastProgramEnrollments && type === 'past' && (
        <div className="px-12 py-6 gap-1 flex flex-col items-center border border-border rounded-2xl">
          <span className="text-xl font-bold">
            You have no past programs yet
          </span>
        </div>
      )}
      {hasGraduatedProgramEnrollments &&
        type === 'graduated' &&
        graduatedProgramEnrollments.map((enrollment) => (
          <ProgramEnrollmentView
            enrollment={enrollment}
            key={enrollment.id}
            coursesHiddenByDefault
          />
        ))}
    </>
  );
};
